"use client"

import ClipboardMSVG from "images/Icons/ClipboardM.inline.svg"
import GraphClosedMSVG from "images/Icons/GraphClosedM.inline.svg"
import SetMSVG from "images/Icons/SetClosedM.inline.svg"
import StacksMSVG from "images/Icons/StacksM.inline.svg"
import PortraitIMG from "images/brokers/mainPoints/InlinePortrait.png"
import StaticImage from "library/StaticImage"
import { css, f, styled } from "library/styled"
import { useMedia } from "library/useMedia"
import colors from "styles/colors"
import textStyles from "styles/text"

const pointsData = [
	{
		id: 1,
		title: "Fast and Simple Quoting",
		icon: ClipboardMSVG,
		description:
			"We know it's tough to get data from small groups—so we don’t require IHQs or claims data. We deliver firm quotes within hours.",
	},
	{
		id: 2,
		title: "Easy Implementation",
		icon: StacksMSVG,
		description:
			"Implementation can be tricky with small groups, often requiring multiple parties to collaborate. We help everyone stay on top to seamlessly go from quote RFP to bound policy.",
	},
	{
		id: 3,
		title: "Live Insights",
		icon: GraphClosedMSVG,
		description:
			"Our dashboards provide real-time reporting with clear, actionable steps to improve your groups’ performance.",
	},
	{
		id: 4,
		title: "Long-Term Thinking",
		icon: SetMSVG,
		description:
			"We focus on stable, consistent pricing from the start, with no surprises at renewal. As long-term partners, we’re committed to helping you win and retain groups for years to come.",
	},
]

export default function MainPoints() {
	const portraitDimensions = useMedia([88, 47], [88, 47], [114, 60], [71, 34])

	return (
		<Wrapper>
			<Inner>
				<Title>
					Your Trusted
					<br />
					Partner for Small Group
					<Portrait
						src={PortraitIMG}
						width={portraitDimensions[0]}
						height={portraitDimensions[1]}
						alt="A smiling man with a beard wearing a black shirt against a beige background."
					/>
					Success
				</Title>
				<Points>
					{pointsData.map((point) => {
						const Icon = point.icon

						return (
							<Point key={point.id}>
								<PointHeader>
									<PointIcon>
										<Icon />
									</PointIcon>
									<PointTitle>{point.title}</PointTitle>
								</PointHeader>
								<PointDescription>{point.description}</PointDescription>
							</Point>
						)
					})}
				</Points>
			</Inner>
		</Wrapper>
	)
}

const Wrapper = styled("section", {
	...f.responsive(css`
		display: grid;
		place-items: center;
		width: 100%;
	`),
})

const Inner = styled("div", {
	...f.responsive(css`
		width: 1440px;
		padding: 104px 32px 104px 64px;
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
	`),
	...f.tablet(css`
		width: 1024px;
		padding: 104px 32px;
		flex-direction: column;
		justify-content: unset;
		align-items: unset;
		gap: 96px;
	`),
	...f.mobile(css`
		width: 375px;
		padding: 64px 24px 80px;
		flex-direction: column;
		justify-content: unset;
		align-items: unset;
		gap: 64px;
	`),
})

const Title = styled("h2", {
	...f.responsive(css`
		display: inline-block;
		${textStyles.h5};
		color: ${colors.black100};
		width: 427px;
	`),
	...f.tablet(css`
		${textStyles.h3};
		width: 660px;
	`),
	...f.mobile(css`
		${textStyles.h6};
		width: 327px;
	`),
})

const Portrait = styled(StaticImage, {
	...f.responsive(css`
		display: inline;
		width: 88px;
		height: 47px;
		margin: 0 12px;
		border-radius: 12px;
		transform: translateY(10px);
	`),
	...f.tablet(css`
		width: 114px;
		height: 60px;
		transform: translateY(6px);
	`),
	...f.mobile(css`
		width: 71px;
		height: 34px;
		border-radius: 6px;
		transform: translateY(4px);
		margin: 0 7px;
	`),
})

const Points = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		width: 859px;
	`),
	...f.tablet(css`
		width: 960px;
	`),
	...f.mobile(css`
		width: 327px;
	`),
})

const Point = styled("div", {
	...f.unresponsive(css`
		border-top: 1px solid ${colors.green100};

		&:last-of-type {
			border-bottom: 1px solid ${colors.green100};
		}
	`),
	...f.responsive(css`
		display: flex;
		justify-content: space-between;
		align-items: flex-end;
		padding: 48px 32px;
	`),
	...f.mobile(css`
		flex-direction: column;
		justify-content: unset;
		align-items: flex-start;
		gap: 40px;
		padding: 32px 20px;
	`),
})

const PointHeader = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		gap: 24px;
		width: fit-content;

		svg {
			path {
				stroke: ${colors.green200};
			}
		}
	`),
})

const PointIcon = styled("div", {
	...f.responsive(css`
		width: 32px;
		height: 32px;
		display: grid;
		place-items: center;
	`),
})

const PointTitle = styled("h4", {
	...f.responsive(css`
		${textStyles.bodyXL};
		color: ${colors.green500};
	`),
	...f.mobile(css`
		${textStyles.bodyL};
	`),
})

const PointDescription = styled("p", {
	...f.responsive(css`
		${textStyles.bodyR};
		color: ${colors.green400};
		width: 388px;
	`),
	...f.mobile(css`
		width: 285px;
	`),
})
